import {PLAN, DEFAULT_SPLIT, SPLIT_OPTIONS, HOURS_MIN, priceForHours, upfrontForHours, deferredForHours, alacarteForHours, discountForHours, roundHours} from '@/constants/packages';

// Build Your Own Package wizard state. Held in the store so the step panels
// and the sticky summary card share one source of truth, and so selections
// survive moving back and forth between steps.
export default {
    state: {
        pkgHours: 0,            // chosen package size, in 10-hour steps (0 = not chosen)
        // Focus areas: real course categories [{id, title}], used to
        // recommend courses (matched by category id, never by title text)
        pkgFocusCategories: [],
        // [{key, id, title, name, organization, hours}] in add order; `name`
        // is the unique enrollment name (base title when not customized) and
        // `key` derives from it, so one course can appear as several
        // uniquely-named instances
        pkgSelections: [],
        pkgPay: null,           // 'full' | 'plan'
        pkgSplitUpfront: DEFAULT_SPLIT.upfront,  // chosen upfront $/hr; the split sets the deferred rate
        // The user's sales tax situation (WA destination-based); tax is
        // included in bundle prices and itemized for awareness
        pkgTaxRate: 0,
        pkgTaxJurisdiction: null
    },
    getters: {
        pkgHours: state => state.pkgHours,
        pkgFocusCategories: state => state.pkgFocusCategories,
        pkgSelections: state => state.pkgSelections,
        pkgPay: state => state.pkgPay,
        pkgPlan: () => PLAN,
        pkgTargetHours: state => state.pkgHours,
        pkgAllocatedHours: state => state.pkgSelections.reduce((sum, s) => sum + s.hours, 0),
        pkgRemainingHours: (state, getters) => getters.pkgTargetHours - getters.pkgAllocatedHours,
        pkgPrice: state => priceForHours(state.pkgHours, state.pkgSplitUpfront),
        pkgUpfront: state => upfrontForHours(state.pkgHours, state.pkgSplitUpfront),
        pkgDeferred: state => deferredForHours(state.pkgHours, state.pkgSplitUpfront),
        pkgSplitUpfront: state => state.pkgSplitUpfront,
        pkgAlacarte: state => alacarteForHours(state.pkgHours),
        pkgDiscount: state => discountForHours(state.pkgHours, state.pkgSplitUpfront),
        pkgSavings: (state, getters) => getters.pkgAlacarte - getters.pkgPrice,
        // Whole-dollar payments; the final month collects the (smaller)
        // remainder so the plan always totals exactly the package cost
        pkgMonthly: (state, getters) => state.pkgHours ? Math.ceil(getters.pkgUpfront / PLAN.months) : 0,
        pkgMonthlyLast: (state, getters) => state.pkgHours ? getters.pkgUpfront - getters.pkgMonthly * (PLAN.months - 1) : 0,
        pkgFocusLine: state => {
            const titles = state.pkgFocusCategories.map(c => c.title);
            if (!titles.length) return '';
            return titles.slice(0, 3).join(' · ') + (titles.length > 3 ? ` · +${titles.length - 3} more` : '');
        },
        pkgTaxRate: state => state.pkgTaxRate,
        pkgTaxJurisdiction: state => state.pkgTaxJurisdiction,
        pkgTaxIncluded: (state, getters) => Math.round(getters.pkgPrice * state.pkgTaxRate * 100) / 100
    },
    mutations: {
        setPkgHours(state, hours) {
            let rounded = roundHours(hours);
            if (rounded > 0 && rounded < HOURS_MIN) {
                // stepping up from empty lands on the minimum; dropping below
                // the minimum clears the selection
                rounded = state.pkgHours >= HOURS_MIN ? 0 : HOURS_MIN;
            }
            state.pkgHours = rounded;
        },
        togglePkgFocusCategory(state, category) {
            const index = state.pkgFocusCategories.findIndex(c => c.id === category.id);
            if (index === -1) {
                state.pkgFocusCategories.push({id: category.id, title: category.title});
            } else {
                state.pkgFocusCategories.splice(index, 1);
            }
        },
        addPkgCourse(state, selection) {
            let key = selection.id + ':' + selection.name.trim().toLowerCase();
            if (state.pkgSelections.some(s => s.key === key)) {
                // Named-in-setup courses (allow_custom_entry) repeat under the
                // same display name — uniquify the key; named selections are
                // true duplicates and are ignored
                if (!selection.named_in_setup) return;
                let n = 2;
                while (state.pkgSelections.some(s => s.key === key + ':' + n)) n++;
                key = key + ':' + n;
            }
            state.pkgSelections.push({...selection, key});
        },
        setPkgCourseHours(state, {key, hours}) {
            const selection = state.pkgSelections.find(s => s.key === key);
            if (selection) selection.hours = hours;
        },
        removePkgCourse(state, key) {
            const index = state.pkgSelections.findIndex(s => s.key === key);
            if (index !== -1) state.pkgSelections.splice(index, 1);
        },
        setPkgPay(state, pay) {
            state.pkgPay = pay;
        },
        setPkgSplit(state, rate) {
            if (SPLIT_OPTIONS.some(o => o.upfront === rate)) state.pkgSplitUpfront = rate;
        },
        setPkgTax(state, {rate, jurisdiction}) {
            state.pkgTaxRate = rate || 0;
            state.pkgTaxJurisdiction = jurisdiction || null;
        },
        resetPkg(state) {
            state.pkgHours = 0;
            state.pkgSplitUpfront = DEFAULT_SPLIT.upfront;
            state.pkgFocusCategories = [];
            state.pkgSelections = [];
            state.pkgPay = null;
        }
    }
};
